import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import {
  getDiffAbsoluteDays,
  transformMinutesInHours,
  formatTime,
} from 'Components/widgets/transport-details/transportUtils';
import { themes } from 'Components/widgets/be-transport-details/BeTransportDetails.themes';
import { capitalizeName } from 'Utils/label/labelUtils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
type TransportDetailsItineraryProps = {
  theme: 'mobile' | 'desktop';
  transportDetailEnriched: TransportDetailEnriched;
};
export function TransportDetailsItinerary({
  theme,
  transportDetailEnriched,
}: TransportDetailsItineraryProps) {
  const departureTime =
    formatTime(transportDetailEnriched.departure.time || '', t('common.timeFormat')) || '';
  const arrivalTime =
    formatTime(transportDetailEnriched.arrival.time || '', t('common.timeFormat')) || '';
  const departureLabel = capitalizeName(transportDetailEnriched.departure.airportLabel);
  const arrivalLabel = capitalizeName(transportDetailEnriched.arrival.airportLabel);
  const arrivalDaysAfter = getDiffAbsoluteDays(
    transportDetailEnriched.departure.date,
    transportDetailEnriched.arrival.date,
  );
  const { isRTL } = useResponsive();
  const { thItinerary } = themes()[theme];
  return (
    <div className="flex w-full" data-testid="TransportDetailsItinerary">
      <div className={classnames('flex-none text-end', thItinerary)}>
        <p className="text-16 font-semibold leading-normal">{departureTime}</p>
        <p className="invisible text-12">({t('common.transport.localHour')})</p>
        <p
          style={
            !departureTime
              ? {
                  marginTop: '22px',
                }
              : {}
          }
          className="text-14 leading-1"
        >
          {departureLabel}
        </p>
      </div>
      <div className="grow px-10 pt-[28px] text-center">
        {Number(transportDetailEnriched.durationInMin) > 0 ? (
          <p className="text-12 leading-1 text-mediumGrey">
            {transformMinutesInHours(transportDetailEnriched.durationInMin || 0, isRTL)}
          </p>
        ) : (
          <div className="pt-10"></div>
        )}
        {transportDetailEnriched.stops > 0 ? (
          <div className="my-10 flex w-full items-center">
            <div className="w-full rounded-l-24 border-b border-t border-solid border-lightGrey" />
            <div className="mx-2 rounded-full border-2 border-solid border-orange" />
            <div className="w-full rounded-r-24 border-b border-t border-solid border-lightGrey" />
          </div>
        ) : (
          <div className="my-10 w-full rounded-24 border-b border-t border-solid border-lightGrey" />
        )}

        {transportDetailEnriched.stops > 0 ? (
          <p className="text-12 leading-1 text-orange">
            {`${transportDetailEnriched.stops} ${t('common.transport.connection')}`}
          </p>
        ) : (
          <p className="text-12 leading-1 text-green">{t('common.transport.direct')}</p>
        )}
      </div>
      <div className={classnames('flex-none', thItinerary)}>
        <div className="flex items-center gap-4 leading-normal">
          <span className="text-16 font-semibold">{arrivalTime}</span>
          {arrivalDaysAfter !== null && arrivalDaysAfter > 0 && (
            <span
              data-testid="TransportDetailsItineraryArrivalDaysAfter"
              className="h-full rounded-pill bg-sienna px-10 py-2 text-12 font-bold text-white"
            >
              +
              {theme === 'mobile'
                ? arrivalDaysAfter
                : t('common.dateTime.day', {
                    count: arrivalDaysAfter,
                  })}
            </span>
          )}
        </div>
        <p className="text-12">({t('common.transport.localHour')})</p>
        <p
          style={
            !arrivalTime
              ? {
                  marginTop: '22px',
                }
              : {}
          }
          className="text-14 leading-1"
        >
          {arrivalLabel}
        </p>
      </div>
    </div>
  );
}
